/**
 * Bus d'événements de SESSION (module feuille, aucune dépendance).
 *
 * Sert un seul cas : une réponse **401** reçue par `apiFetch` HORS des routes
 * d'authentification signifie que la session du slot visé a été invalidée
 * PENDANT la navigation (lien d'accès révoqué — cf. v1.11.0 « révoquer un lien
 * d'accès déconnecte ses porteurs » —, cookie expiré, purge serveur). Sans ce
 * signal, l'utilisateur voit toutes ses actions échouer avec un « Erreur »
 * générique et reste bloqué jusqu'à un F5 manuel : il n'est JAMAIS renvoyé vers
 * l'écran de connexion.
 *
 * `apiFetch` (couche transport) ne connaît pas React ; `AuthProvider` (couche
 * état) ne connaît pas le transport. Ce bus les relie sans créer de cycle
 * d'import : `lib/http.ts` n'importe QUE ce fichier, jamais `auth/`.
 *
 * Le slot visé VOYAGE avec l'événement (multi-login) : l'abonné décide s'il le
 * concerne. Un 401 sur un slot qui n'est plus l'actif (requête en vol partie
 * avant une bascule de compte) ne doit rien casser.
 */

/** Détail d'un 401 applicatif : slot visé par la requête (ou `null` si aucun). */
export interface UnauthorizedEvent {
  /** Valeur de `X-Active-Slot` au moment de la requête, ou `null`. */
  slot: number | null;
}

type UnauthorizedListener = (event: UnauthorizedEvent) => void;

const listeners = new Set<UnauthorizedListener>();

/**
 * S'abonne aux 401 applicatifs. Retourne la fonction de désabonnement (à appeler
 * au démontage). Plusieurs abonnés sont permis (tous notifiés).
 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Signale un 401 applicatif. Ne lève JAMAIS : un abonné fautif ne doit pas faire
 * échouer la requête HTTP qui l'a déclenché (l'appelant a son propre traitement
 * d'erreur, on ne le court-circuite pas).
 */
export function notifyUnauthorized(event: UnauthorizedEvent): void {
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch {
      // Abonné fautif : ignoré (le flux d'erreur de l'appelant prime).
    }
  }
}
